import React from 'react'
import { flex, grid } from 'styled-system/patterns'
import { motion } from 'motion/react'
import { Image, Text, PriceText } from '@mindfulchefuk/design-system'
import { useChooseRecipesContext } from '@mindfulchefuk/onboarding/context/ChooseRecipesContext/ChooseRecipesContext'
import { TBasketRecipeFullDetails } from '@mindfulchefuk/onboarding/helpers/getBasketRecipeDataFromBasketIDs/getBasketRecipeDataFromBasketIDs'
import { OnboardingCartDrawerRecipeActionButtons } from '@mindfulchefuk/onboarding/components/OnboardingCartDrawer/OnboardingCartBasket/OnboardingCartDrawerRecipe/OnboardingCartDrawerRecipeActionButtons'

const itemVariants = {
  hide: { opacity: 0, x: 100 },
  show: (custom: { index: number }) => {
    const { index } = custom

    return {
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.5 + index * 0.2,
        duration: 0.2,
        ease: 'easeInOut',
        when: 'beforeChildren',
      },
      height: 'auto',
    }
  },
  exit: { opacity: 0, x: 100, height: 0 },
}

export const OnboardingCartDrawerRecipe = ({
  index,
  isCartDrawerOpen,
  recipe,
}: {
  index: number
  isCartDrawerOpen: boolean
  recipe: TBasketRecipeFullDetails
}) => {
  const { imageUrl: image, price, basketDisplayTitle, quantity } = recipe
  const {
    handleAddRecipe,
    handleRemoveRecipe,
    maximumRecipesForPortion,
    totalNumberOfRecipes,
  } = useChooseRecipesContext()

  const recipeHasQuantity = recipe.quantity >= 1
  const shouldAnimate = recipeHasQuantity && isCartDrawerOpen

  return (
    <motion.li
      className={flex({
        alignItems: 'flex-start',
        borderBottomWidth: '1px',
        borderColor: 'aubergine.50',
        justifyContent: 'space-between',
        marginBottom: { base: 3 },
        position: 'relative',
        _last: {
          border: 'none',
          marginBottom: 0,
        },
      })}
      custom={{ index: 0 + index }}
      animate={shouldAnimate ? 'show' : 'hide'}
      transition={
        shouldAnimate ? null : { duration: 0.2, delay: 0.5 + index * 0.2 }
      }
      variants={itemVariants}
    >
      <div
        className={grid({
          gap: 2.5,
          gridTemplateColumns: '88px 1fr',
          paddingBottom: 3,
          position: 'relative',
          width: '100%',
        })}
      >
        <Image
          alt=""
          data-testid="basket-item-image"
          height={88}
          src={image}
          useFallback
          width={88}
        />

        <div
          className={grid({
            gridTemplateColumns: '1fr',
            alignContent: 'space-between',
          })}
        >
          <Text
            as="h2"
            variant="body-md"
            color="pepper"
            mb={6}
            textTransform="capitalize"
          >
            {basketDisplayTitle}
          </Text>

          <div
            className={flex({
              wrap: 'wrap',
              paddingTop: 2,
              gap: 2,
              justifyContent: 'space-between',
            })}
          >
            <OnboardingCartDrawerRecipeActionButtons
              addItem={() => handleAddRecipe({ recipe, clickOrigin: 'basket' })}
              isAbleToAddRecipes={
                totalNumberOfRecipes < maximumRecipesForPortion
              }
              quantity={quantity}
              recipeTitle={basketDisplayTitle}
              removeItem={() =>
                handleRemoveRecipe({ recipe, clickOrigin: 'basket' })
              }
            />

            <PriceText price={price * quantity} variant="body-md-500" />
          </div>
        </div>
      </div>
    </motion.li>
  )
}
